import type { useIncomingLinksQuery$data } from './__generated__/useIncomingLinksQuery.graphql.ts';
import {
	getIncomingLinksPageObservedEvent,
	type IncomingLinksPageOutcome,
	type IncomingLinksPagePhase,
} from './incomingLinksAnalytics.ts';
import { extractIncomingLinkUrl } from '../utils/extractIncomingLinkUrl.ts';
import type { CuratedCypherQueryData } from '../utils/readIncomingLinkUrls.ts';

export type IncomingLinksRawPageAttributes = {
	rawPage: 'observed' | 'unavailable';
	rawEdgeCount?: number;
	urlOccurrenceCount?: number;
	uniqueUrlCount?: number;
	duplicateUrlCount?: number;
	previousPageUrlOccurrenceCount?: number;
	unavailableUrlColumnCount?: number;
};

// CCINT-9578: updateQuery receives the raw page on both interop paths; the returned promise can contain merged pages.
export const observeIncomingLinksRawPage = (
	data: CuratedCypherQueryData | undefined,
	previousUrls: Set<string>,
): IncomingLinksRawPageAttributes => {
	const edges = data?.graphStore_cypherQueryV2?.edges;
	if (!Array.isArray(edges)) {
		return { rawPage: 'unavailable' };
	}
	const pageUrls = new Set<string>();
	let urlOccurrenceCount = 0;
	let previousPageUrlOccurrenceCount = 0;
	let unavailableUrlColumnCount = 0;
	for (const edge of edges) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in returned edges plus columns; each column is visited once
		for (const column of edge?.node?.columns ?? []) {
			const url = extractIncomingLinkUrl(column?.value?.data);
			if (!url) {
				unavailableUrlColumnCount++;
				continue;
			}
			urlOccurrenceCount++;
			if (previousUrls.has(url)) {
				previousPageUrlOccurrenceCount++;
			}
			pageUrls.add(url);
		}
	}
	for (const url of pageUrls) previousUrls.add(url);
	return {
		rawPage: 'observed',
		rawEdgeCount: edges.length,
		urlOccurrenceCount,
		uniqueUrlCount: pageUrls.size,
		duplicateUrlCount: urlOccurrenceCount - pageUrls.size,
		previousPageUrlOccurrenceCount,
		unavailableUrlColumnCount,
	};
};

export const incomingLinksObservationTime = (): number | undefined => {
	try {
		const time = performance.now();
		return Number.isFinite(time) ? time : undefined;
	} catch {
		return undefined;
	}
};

export type IncomingLinksPageObservation = {
	invocations: number;
	requestedCursors: Map<string, { invocations: number; outcome?: IncomingLinksPageOutcome }>;
	responseCursors: Set<string>;
	urls: Set<string>;
};

export type IncomingLinksPageEventFactory = (
	event: ReturnType<typeof getIncomingLinksPageObservedEvent>,
) => {
	fire: () => void;
};

export function createIncomingLinksPageObservation(): IncomingLinksPageObservation {
	return {
		invocations: 0,
		requestedCursors: new Map(),
		responseCursors: new Set(),
		urls: new Set(),
	};
}

type ResponseCursorState = 'terminal' | 'missing' | 'new' | 'repeated' | 'unknown';

function responseCursorState(
	info: { readonly hasNextPage?: unknown } | null | undefined,
	nextCursor: string | null | undefined,
	after: string,
	seenCursors: Set<string>,
): ResponseCursorState {
	if (!info || typeof info.hasNextPage !== 'boolean') {
		return 'unknown';
	}
	if (!info.hasNextPage) {
		return 'terminal';
	}
	if (!nextCursor) {
		return 'missing';
	}
	if (nextCursor === after || seenCursors.has(nextCursor)) {
		return 'repeated';
	}
	return 'new';
}

export type StartIncomingLinksPageObservationOptions = {
	after: string;
	phase: IncomingLinksPagePhase;
	observation: IncomingLinksPageObservation;
	createEvent: IncomingLinksPageEventFactory;
};

export type IncomingLinksPageAnalytics = {
	captureRawPage: (data: useIncomingLinksQuery$data | undefined) => void;
	resolved: (result: { error?: unknown; errors?: readonly unknown[] }) => void;
	rejected: (error: unknown) => void;
};

export function startIncomingLinksPageObservation({
	after,
	phase,
	observation,
	createEvent,
}: StartIncomingLinksPageObservationOptions): IncomingLinksPageAnalytics {
	const priorCursor = observation.requestedCursors.get(after);
	const cursorInvocationIndex = (priorCursor?.invocations ?? 0) + 1;
	const pageInvocationIndex = ++observation.invocations;
	const previousCursorOutcome = priorCursor ? (priorCursor.outcome ?? 'pending') : 'none';
	const cursor: { invocations: number; outcome?: IncomingLinksPageOutcome } = {
		invocations: cursorInvocationIndex,
	};
	observation.requestedCursors.set(after, cursor);
	const started = incomingLinksObservationTime();
	let rawPage: IncomingLinksRawPageAttributes = { rawPage: 'unavailable' };
	let capturedRawPage = false;
	let nextCursorState: ResponseCursorState = 'unknown';
	const report = (outcome: IncomingLinksPageOutcome) => {
		cursor.outcome = outcome;
		try {
			const finished = incomingLinksObservationTime();
			let duration: { durationMs?: number } = {};
			if (started !== undefined && finished !== undefined) {
				duration = { durationMs: Math.round(Math.max(0, finished - started)) };
			}
			createEvent(
				getIncomingLinksPageObservedEvent({
					phase,
					outcome,
					countingVersion: 1,
					pageInvocationIndex,
					cursorInvocationIndex,
					previousCursorOutcome,
					responseCursorState: nextCursorState,
					transportAttempts: 'unknown',
					visitAssociation: 'unknown',
					...rawPage,
					...duration,
				}),
			).fire();
		} catch {
			// An observation failure must not turn a usable page into a failed fetch.
		}
	};
	return {
		captureRawPage: (data: useIncomingLinksQuery$data | undefined) => {
			if (capturedRawPage) {
				return;
			}
			capturedRawPage = true;
			try {
				// The promise may contain merged pages, so count the raw callback data before cache append.
				rawPage = observeIncomingLinksRawPage(data, observation.urls);
				const info = data?.graphStore_cypherQueryV2?.pageInfo;
				const nextCursor = info?.endCursor;
				nextCursorState = responseCursorState(info, nextCursor, after, observation.responseCursors);
				if (nextCursor) {
					observation.responseCursors.add(nextCursor);
				}
			} catch {
				rawPage = { rawPage: 'unavailable' };
			}
		},
		resolved: (result: { error?: unknown; errors?: readonly unknown[] }) =>
			report(result.error || result.errors?.length ? 'partial' : 'succeeded'),
		rejected: (error: unknown) =>
			report(error instanceof Error && error.name === 'AbortError' ? 'cancelled' : 'failed'),
	};
}
